import { NumberField } from "@/components/ui/number-field";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { WIDGET_REGISTRY } from "@/lib/widget-registry";
import { useState, useEffect } from "react";

type DashboardWidget = {
  id: string;
  tenantId: string;
  userId: string;
  widgetType: string;
  title: string | null;
  size: 'full' | 'half' | 'quarter';
  orderIndex: number;
  column: number;
  isVisible: boolean;
  settings: Record<string, any>;
  createdAt: Date | string;
  updatedAt: Date | string;
};

interface EditWidgetDialogProps {
  widget: DashboardWidget | null;
  isOpen: boolean;
  onClose: () => void;
}

export function EditWidgetDialog({ widget, isOpen, onClose }: EditWidgetDialogProps) {
  const [title, setTitle] = useState("");
  const [size, setSize] = useState<'full' | 'half' | 'quarter'>('full');
  const [maxItems, setMaxItems] = useState<number>(5);

  // Scorecard-specific settings
  const [period, setPeriod] = useState<string>("weekly");
  const [rangeType, setRangeType] = useState<string>("rolling");
  const [showTrend, setShowTrend] = useState(true);

  useEffect(() => {
    if (widget) {
      setTitle(widget.title || "");
      setSize(widget.size);
      setMaxItems(widget.settings?.maxItems || 5);
      setPeriod(widget.settings?.period || "weekly");
      setRangeType(widget.settings?.rangeType || "rolling");
      setShowTrend(widget.settings?.showTrend ?? true);
    }
  }, [widget]);

  const updateWidgetMutation = useMutation({
    mutationFn: async (updates: Partial<DashboardWidget>) => {
      return apiRequest("PATCH", `/api/dashboard/widgets/${widget!.id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
      onClose();
    },
    onError: () => {
    },
  });

  const handleSave = () => {
    if (!widget) return;

    const config = WIDGET_REGISTRY[widget.widgetType];
    const updates: Partial<DashboardWidget> = {
      title: title || null,
      size,
    };

    if (widget.widgetType === 'scorecard') {
      updates.settings = {
        ...widget.settings,
        period,
        rangeType,
        showTrend,
        // Preserve existing history — changing settings should not wipe it
        history: widget.settings?.history ?? [],
      };
    } else if (config?.defaultSettings?.maxItems !== undefined) {
      updates.settings = { ...widget.settings, maxItems };
    }

    updateWidgetMutation.mutate(updates);
  };

  if (!widget) return null;

  const config = WIDGET_REGISTRY[widget.widgetType];
  if (!config) return null;

  const hasMaxItemsSetting = config.defaultSettings?.maxItems !== undefined;
  const isScorecardWidget = widget.widgetType === 'scorecard';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Widget</DialogTitle>
          <DialogDescription>
            Customize your {config.name} widget
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="widget-title">Widget Title</Label>
            <Input
              id="widget-title"
              placeholder={config.defaultTitle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="input-widget-title"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use default title
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="widget-size">Widget Size</Label>
            <Select value={size} onValueChange={(value: any) => setSize(value)}>
              <SelectTrigger id="widget-size" data-testid="select-widget-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.allowedSizes.includes('full') && (
                  <SelectItem value="full">Full Width</SelectItem>
                )}
                {config.allowedSizes.includes('half') && (
                  <SelectItem value="half">Half Width</SelectItem>
                )}
                {config.allowedSizes.includes('quarter') && (
                  <SelectItem value="quarter">Quarter Width</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {hasMaxItemsSetting && (
            <div className="space-y-2">
              <Label htmlFor="max-items">Maximum Items</Label>
              <NumberField
                id="max-items"
                min={1}
                max={20}
                emptyValue={5}
                value={maxItems}
                onValueChange={(v) => setMaxItems(v ?? 5)}
                data-testid="input-max-items"
              />
              <p className="text-xs text-muted-foreground">
                Number of items to display in this widget
              </p>
            </div>
          )}

          {isScorecardWidget && (
            <>
              <div className="space-y-2">
                <Label htmlFor="scorecard-period">Time Period</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger id="scorecard-period" data-testid="select-scorecard-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Which time window to score your performance against
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="scorecard-range">Range Type</Label>
                <Select value={rangeType} onValueChange={setRangeType}>
                  <SelectTrigger id="scorecard-range" data-testid="select-scorecard-range">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rolling">Rolling (last N days)</SelectItem>
                    <SelectItem value="calendar">Calendar (current period)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Rolling counts back from today. Calendar uses the current week, month, or quarter boundary.
                </p>
              </div>

              <div className="flex items-center justify-between py-1">
                <div className="space-y-0.5">
                  <Label htmlFor="scorecard-trend">Show Trend Line</Label>
                  <p className="text-xs text-muted-foreground">
                    Display a chart of your score history over time
                  </p>
                </div>
                <Switch
                  id="scorecard-trend"
                  checked={showTrend}
                  onCheckedChange={setShowTrend}
                  data-testid="switch-scorecard-trend"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateWidgetMutation.isPending}
            data-testid="button-save-widget"
          >
            {updateWidgetMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
